import { formatCodeableConcept } from '@medplum/core';
import { MedicationRequestFragment } from '@/pages/PatientTimelinePage/graphql';

/**
 * Gets the medication name from a MedicationRequest fragment
 * @param medication - MedicationRequest fragment
 * @returns Medication name or "Unknown medication" if not found
 */
export function getMedicationName(medication: MedicationRequestFragment): string {
  // Try medicationCodeableConcept first
  if (medication.medicationCodeableConcept?.text) {
    return medication.medicationCodeableConcept.text;
  }
  if (medication.medicationCodeableConcept) {
    const formatted = formatCodeableConcept(medication.medicationCodeableConcept);
    if (formatted) return formatted;
  }

  // Try medicationReference
  if (medication.medicationReference?.display) {
    return medication.medicationReference.display;
  }

  return 'Unknown medication';
}

/**
 * Gets the dosage information from a MedicationRequest fragment
 * @param medication - MedicationRequest fragment
 * @returns Dosage string or empty string if not found
 */
export function getDosage(medication: MedicationRequestFragment): string {
  const dosage = medication.dosageInstruction?.[0];
  if (!dosage) return '';

  // Try text first
  if (dosage.text) return dosage.text;

  // Try doseQuantity
  const doseQuantity = dosage.doseAndRate?.[0]?.doseQuantity;
  if (doseQuantity?.value && doseQuantity?.unit) {
    return `${doseQuantity.value} ${doseQuantity.unit}`;
  }
  if (doseQuantity?.value) {
    return `${doseQuantity.value}`;
  }

  // Try timing
  if (dosage.timing?.code?.text) {
    return dosage.timing.code.text;
  }

  return '';
}

/**
 * Gets the provenance information from a MedicationRequest fragment
 * @param medication - MedicationRequest fragment
 * @returns Provenance string (e.g., "Prescribed via DoseSpot") or empty string if not DoseSpot
 */
export function getProvenance(medication: MedicationRequestFragment): string {
  // Only show provenance for DoseSpot
  const sourceValue = medication.recorder?.identifier?.value;
  if (sourceValue?.toLowerCase() === 'dosespot') {
    return 'Prescribed via DoseSpot';
  }

  return '';
}
